import React from "react";
import { Box, Container, Link, Paper, Typography } from "@material-ui/core";
import {
  isInternationalOnlyDomain,
  useDomainFeatures,
} from "../../context/DomainFeaturesContext";

const pageWrap = { paddingTop: 28, paddingBottom: 48 };
const card = {
  padding: "32px 36px 40px",
  borderRadius: 16,
  border: "1px solid #e8eef4",
  boxShadow: "0 8px 28px rgba(15, 43, 74, 0.06)",
};
const titleStyle = { fontWeight: 800, color: "#0F3B62", letterSpacing: "-0.02em" };
const metaStyle = { color: "#64748b", marginTop: 8, lineHeight: 1.6 };
const introStyle = {
  lineHeight: 1.8,
  color: "#334155",
  marginTop: 16,
  background: "#f7fafc",
  border: "1px solid #e8eef4",
  borderRadius: 12,
  padding: "14px 16px",
};
const headingStyle = {
  fontWeight: 700,
  color: "#0F3B62",
  marginTop: 28,
  fontSize: "1.05rem",
};
const bodyStyle = { lineHeight: 1.8, color: "#334155", marginTop: 8 };
const listStyle = { margin: "8px 0 0", paddingLeft: 22, color: "#334155", lineHeight: 1.8 };

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="h6" style={headingStyle}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function Term() {
  const { wwwDomain, supportEmail, domainName } = useDomainFeatures();
  const isUs = isInternationalOnlyDomain(domainName);

  const copy = isUs
    ? {
        lastUpdated: "Last updated on November 25, 2024",
        introBanner: `These terms and conditions apply to ${wwwDomain} School Activity Monitor Software. The clauses below are typical of software agreements and are tailored for educational use.`,
        welcome: `Welcome to ${wwwDomain}, an activity monitoring tool designed specifically for Montessori schools to enhance the educational experience. By using the Software, you agree to comply with and be bound by the following Terms and Conditions. If you do not agree with these Terms, please do not use the Software.`,
        licenseLead:
          "The Software is licensed, not sold. Srshta Tech Solutions Pvt Ltd. grants the School a non-exclusive, non-transferable, limited license to use the Software for educational purposes. This license is subject to the following conditions:",
        governing: [
          "These Terms and Conditions are governed by the laws of the United States of America.",
          "Any disputes arising from the use of the Software will first be subject to mediation or arbitration before litigation in a court of competent jurisdiction in the United States.",
        ],
        contactLead:
          "For any questions or concerns regarding these Terms and Conditions, please contact us at:",
      }
    : {
        lastUpdated: "Last updated on 25th Nov, 2024",
        introBanner: `Below are draft terms and conditions for ${wwwDomain} School Activity Monitor Software. The content includes key clauses often found in software agreements, tailored specifically for educational environments and software use.`,
        welcome: `Welcome to ${wwwDomain}, an activity monitoring tool designed specifically for Montessori schools to enhance the educational experience. By using the Software, you agree to comply with and be bound by the following Terms and Conditions. If you do not agree with these Terms, please do not use the Software.`,
        licenseLead:
          "The Software is licensed, not sold. Srshta Tech Solutions Pvt Ltd. grants the School a non-exclusive, non-transferable, limited licence to use the Software for educational purposes. This licence is subject to the following conditions:",
        governing: [
          "These Terms and Conditions are governed by the laws in the jurisdiction of Hyderabad, Telangana, India.",
          "Any disputes arising from the use of the Software will be subject to mediation or arbitration before pursuing litigation.",
        ],
        contactLead:
          "For any questions or concerns regarding these Terms and Conditions, please contact us at:",
      };

    return (
    <Container maxWidth="md" style={pageWrap}>
      <Paper elevation={0} style={card}>
        <Typography variant="h4" component="h1" style={titleStyle}>
          Terms &amp; Conditions
        </Typography>
        <Typography variant="body2" style={metaStyle}>
          Montessori School Activity Monitor Software · {copy.lastUpdated}
        </Typography>
        <Typography variant="body2" style={introStyle}>
          {copy.introBanner}
        </Typography>

        <Section title="1. Introduction">
          <Typography variant="body1" style={bodyStyle}>
            {copy.welcome}
          </Typography>
        </Section>

        <Section title="2. Definitions">
          <Box component="ul" style={listStyle}>
            <li>
              <strong>Software:</strong> Refers to the Montessori School Activity Monitor Software provided by Srshta Tech Solutions Pvt Ltd.
            </li>
            <li>
              <strong>User:</strong> Any individual or institution authorized to use the Software.
            </li>
            <li>
              <strong>School:</strong> The Montessori school or educational institution using the Software.
            </li>
            <li>
              <strong>Administrator:</strong> The person(s) designated by the School with permissions to manage and oversee Software use.
            </li>
            <li>
              <strong>Data:</strong> Any data, information, or records collected or generated through the use of the Software.
            </li>
          </Box>
        </Section>

        <Section title="3. License">
          <Typography variant="body1" style={bodyStyle}>
            {copy.licenseLead}
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>The Software may only be used by authorized staff members of the school.</li>
            <li>The Software may not be resold, distributed, or sublicensed to third parties.</li>
            <li>Unauthorized use of the Software is strictly prohibited.</li>
          </Box>
        </Section>

        <Section title="4. User Obligations">
          <Typography variant="body1" style={bodyStyle}>
            By using the Software, the School and its Users agree to:
          </Typography>
          <Box component="ul" style={listStyle}>
            <li>Use the Software solely for educational purposes in accordance with Montessori principles.</li>
            <li>Not use the Software in any manner that may compromise its security or functionality.</li>
            <li>Not modify, reverse engineer, or attempt to derive the source code of the Software.</li>
            <li>Ensure all Users comply with these Terms and Conditions.</li>
          </Box>
        </Section>

        <Section title="5. Data Privacy and Security">
          <Box component="ul" style={listStyle}>
            <li>
              The Software may collect and store data related to student activities. All data is processed and managed in accordance with applicable data protection laws.
            </li>
            <li>
              The school is responsible for ensuring that it has obtained the necessary permissions from parents or guardians before collecting any student data.
            </li>
            <li>
              <strong>Srshta Tech Solutions Pvt Ltd.</strong> is committed to ensuring the security of any data collected by the Software and will take reasonable precautions to protect it from unauthorized access or misuse.
            </li>
          </Box>
        </Section>

        <Section title="6. Restrictions">
          <Box component="ul" style={listStyle}>
            <li>Users may not use the Software to engage in any illegal or unauthorized activities.</li>
            <li>Users shall not attempt to gain unauthorized access to the Software or its underlying systems.</li>
            <li>The Software may not be used in a manner that interferes with the educational environment or disrupts learning.</li>
          </Box>
        </Section>

        <Section title="7. Software Updates and Maintenance">
          <Box component="ul" style={listStyle}>
            <li>
              <strong>Srshta Tech Solutions Pvt Ltd</strong> reserves the right to update, modify, or discontinue the Software at any time, with or without notice.
            </li>
            <li>Updates may be required to maintain the Software’s functionality and security.</li>
            <li>The school is responsible for ensuring that its systems meet the minimum requirements to run the Software.</li>
          </Box>
        </Section>

        <Section title="8. Support and Maintenance">
          <Box component="ul" style={listStyle}>
            <li>
              <strong>Srshta Tech Solutions Pvt Ltd</strong> will provide technical support as outlined in a separate Service Level Agreement (SLA), if applicable.
            </li>
            <li>
              Support requests should be directed to{" "}
              <Link href={`mailto:${supportEmail}`} color="primary">
                {supportEmail}
              </Link>
              .
            </li>
            <li>
              The school agrees to cooperate with <strong>Srshta Tech Solutions Pvt Ltd</strong> during troubleshooting and support activities.
            </li>
          </Box>
        </Section>

        <Section title="9. Fees and Payment">
          <Box component="ul" style={listStyle}>
            <li>The Software may be subject to subscription fees or one-time charges, as outlined in a separate pricing agreement.</li>
            <li>Payment terms and conditions, including billing cycles, are detailed in the applicable pricing agreement.</li>
            <li>Failure to pay fees may result in suspension or termination of access to the Software.</li>
          </Box>
        </Section>

        <Section title="10. Limitation of Liability">
          <Box component="ul" style={listStyle}>
            <li>
              <strong>Srshta Tech Solutions Pvt Ltd</strong> shall not be liable for any indirect, incidental, special, or consequential damages resulting from the use or inability to use the Software.
            </li>
            <li>
              In no event shall <strong>Srshta Tech Solutions Pvt Ltd</strong>’s total liability exceed the amount paid by the School for the Software over the previous 12 months.
            </li>
          </Box>
        </Section>

        <Section title="11. Intellectual Property">
          <Box component="ul" style={listStyle}>
            <li>
              All intellectual property rights related to the Software, including but not limited to trademarks, logos, source code, and documentation, are owned by <strong>Srshta Tech Solutions Pvt Ltd.</strong>
            </li>
            <li>The school is granted no rights other than those expressly stated in this agreement.</li>
          </Box>
        </Section>

        <Section title="12. Termination">
          <Box component="ul" style={listStyle}>
            <li>This agreement may be terminated by either party if there is a material breach of these Terms.</li>
            <li>
              <strong>Srshta Tech Solutions Pvt Ltd.</strong> reserves the right to terminate access to the Software at any time if the school fails to comply with these Terms.
            </li>
                </Box>
        </Section>

        <Section title="13. Governing Law and Dispute Resolution">
          <Box component="ul" style={listStyle}>
            {copy.governing.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </Box>
        </Section>

        <Section title="14. Changes to Terms and Conditions">
          <Box component="ul" style={listStyle}>
            <li>
              <strong>Srshta Tech Solutions Pvt Ltd</strong> reserves the right to modify these Terms and Conditions at any time. Any changes will be communicated to the school via email or through an update in the Software.
            </li>
            <li>Continued use of the Software after any modifications constitutes acceptance of the revised Terms.</li>
          </Box>
        </Section>

        <Section title="15. Contact Information">
          <Typography variant="body1" style={bodyStyle}>
            {copy.contactLead}
          </Typography>
          <Typography variant="body1" style={{ ...bodyStyle, fontWeight: 600, color: "#0F3B62" }}>
            Email:{" "}
            <Link href={`mailto:${supportEmail}`} color="inherit" underline="always">
              {supportEmail}
            </Link>
          </Typography>
        </Section>
      </Paper>
    </Container>
  );
}
